<template>
  <div class="attachments-list">
    <div v-for="attachment in attachments" :key="attachment.id" class="attachment-item">
      <div class="attachment-main-row">
        <div class="file-icon-wrapper" :class="getFileIconClass(attachment.filename)">
          <component
            :is="getFileIcon(attachment.contentType, attachment.filename)"
            class="file-type-icon"
          />
        </div>
        <div class="attachment-details">
          <p class="attachment-filename">{{ attachment.filename }}</p>
          <p class="attachment-meta">
            {{ attachment.humanSize }} · {{ formatDate(attachment.createdAt) }}
            <span
              v-if="attachmentTasks(attachment).length && getTaskUrl"
              class="attachment-task-link"
            >
              ·
              <span class="attachment-task-label">{{ taskLinkLabel(attachment) }}</span>
              <template v-for="(task, index) in attachmentTasks(attachment)" :key="task.id">
                <span v-if="index > 0">, </span>
                <a :href="getTaskUrl(task)" class="task-link">
                  {{ task.title }}
                </a>
              </template>
            </span>
          </p>
        </div>
      </div>
      <div class="attachment-actions">
        <button
          v-if="isImageAttachment(attachment)"
          type="button"
          class="btn-icon-action"
          :title="s__('Attachments|View image')"
          @click="$emit('view-image', attachment)"
        >
          <EyeIcon :size="16" />
        </button>
        <button
          type="button"
          class="btn-icon-action"
          :title="s__('Attachments|Download')"
          :disabled="downloading"
          @click="$emit('download', attachment)"
        >
          <DownloadIcon :size="16" />
        </button>
        <AttachmentSendToTelegramButton
          :disabled="sendingToTelegram"
          @send="$emit('send-to-telegram', attachment)"
        />
        <AttachmentPublicLinkButton
          :attachment="attachment"
          @updated="$emit('public-link-updated', $event)"
        />
        <button
          v-if="canDelete(attachment)"
          type="button"
          class="btn-icon-action btn-icon-delete"
          :title="s__('Attachments|Delete')"
          @click="$emit('delete', attachment)"
        >
          <TrashIcon :size="16" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { s__ } from '@/locale'
import { formatRelativeDate } from '@/utils/dateFormat'
import DownloadIcon from './icons/DownloadIcon.vue'
import TrashIcon from './icons/TrashIcon.vue'
import EyeIcon from './icons/EyeIcon.vue'
import AttachmentSendToTelegramButton from './AttachmentSendToTelegramButton.vue'
import AttachmentPublicLinkButton from './AttachmentPublicLinkButton.vue'

const props = defineProps({
  attachments: {
    type: Array,
    required: true,
  },
  downloading: {
    type: Boolean,
    default: false,
  },
  sendingToTelegram: {
    type: Boolean,
    default: false,
  },
  getFileIconClass: {
    type: Function,
    required: true,
  },
  getFileIcon: {
    type: Function,
    required: true,
  },
  isImageAttachment: {
    type: Function,
    required: true,
  },
  canDelete: {
    type: Function,
    default: () => true,
  },
  getTaskUrl: {
    type: Function,
    default: null,
  },
})

defineEmits(['view-image', 'download', 'send-to-telegram', 'delete', 'public-link-updated'])

const formatDate = formatRelativeDate

const attachmentTasks = attachment => {
  const tasks = attachment.parentTasks?.length
    ? attachment.parentTasks
    : [attachment.parentTask].filter(Boolean)
  const seenTaskIds = new Set()

  return tasks.filter(task => {
    if (!task || props.getTaskUrl?.(task) === '#') return false
    if (seenTaskIds.has(task.id)) return false

    seenTaskIds.add(task.id)
    return true
  })
}

const taskLinkLabel = attachment => {
  return attachmentTasks(attachment).length > 1
    ? s__('Attachments|Tasks:')
    : s__('Attachments|Task:')
}
</script>

<style scoped>
.attachment-task-label {
  margin-right: 4px;
  color: #666;
}
</style>
